/* global runtime */
// State-lagring, virksomhetstilgang, klientcache og abonnement.
// Lastes som klassisk script før backend.jsx og deler dens bindingsmiljø.
const persistCurrentState = async ({ businessId = runtime.activeBusinessId, state = runtime.appState, expectedState = null } = {}) => {
  window.PlummeAccountingJournalRecovery.assertWritable(runtime, businessId);
  if (!runtime.db || !businessId || !runtime.auth?.currentUser || !state) return;
  if (runtime.stateWriteBlockedBusinessIds?.has(businessId)) {
    throw new Error(t('r1e.state.readOnlyDemo'));
  }
  // Generic saves replace client-owned maps; server-owned and personnel maps remain untouched.
  const appState = PlummePersonnelState.stripPersonnelState(state);
  const businessRef = runtime.db.collection('businesses').doc(businessId);
  const stateRef = businessRef.collection('app').doc('state');
  const slot = multiClientStore?.openClient(businessId) || null;
  const isActive = runtime.activeBusinessId === businessId;
  const expectedRevision = slot?.persistedAppStateRevision || (isActive ? runtime.persistedAppStateRevision : '');
  const expectedShardRevisions = slot?.persistedShardRevisions || (isActive ? runtime.persistedShardRevisions : null);
  const staleState = () => new Error(t('r1e.state.changedInOtherTab'));
  await runtime.db.runTransaction(async (transaction) => {
    const snapshot = await transaction.get(stateRef);
    const updatedAt = snapshot.exists ? snapshot.data()?.updatedAt : null;
    const currentRevision = updatedAt?.toDate ? updatedAt.toDate().toISOString() : String(updatedAt || '');
    // Migrerte foretak har kvitteringene i et eget shard (stateShards.js). Uten markor er
    // listen tom, og lagringen er som for: ingen ekstra lesing eller skriving.
    const shardIds = PlummeStateShards.activeStateShards(snapshot.exists ? snapshot.data() : null);
    const canonical = (value) => stripUndefinedDeep(stripServerOwnedState(PlummePersonnelState.stripPersonnelState(mergeState(value)))), stable = PlummeStateContent.stableJson;
    if (expectedRevision && currentRevision && expectedRevision !== currentRevision) {
      // Function responses can precede snapshots: compare the client-owned baseline.
      const signature = (value) => stable(PlummeStateShards.omitStateShardFields(canonical(value), shardIds));
      if (!expectedState || signature(expectedState) !== signature(snapshot.data()?.state)) throw staleState();
    }
    const fullClientState = stripUndefinedDeep(stripServerOwnedState(appState));
    // Shardet skrives bare nar feltene er endret; den gamle kopien i app/state rores ikke.
    const shardWrites = shardIds.length ? await PlummeStateShards.planStateShardWrites({
      transaction, businessRef, activeShardIds: shardIds, state: fullClientState, expectedState, expectedRevisions: expectedShardRevisions, staleState,
      fieldSignature: (value, fields) => { const normalized = canonical(value); return stable(fields.map((field) => normalized[field])); },
    }) : [];
    const clientState = PlummeStateShards.omitStateShardFields(fullClientState, shardIds);
    if (clientState.settings) SERVER_OWNED_SETTINGS_FIELDS.forEach(key => {
      if (Object.prototype.hasOwnProperty.call(snapshot.data()?.state?.settings || {}, key)) clientState.settings[key] = snapshot.data().state.settings[key];
    });
    const meta = {
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      updatedByEmail: runtime.auth.currentUser.email || null,
      updatedBySource: 'client',
      updatedByUserId: runtime.auth.currentUser.uid,
    };
    window.PlummeAccountingJournalRecovery.assertWritable(runtime, businessId); shardWrites.forEach((write) => transaction.set(write.ref, { state: write.state, ...meta }, { mergeFields: [...write.fields.map(key => `state.${key}`), ...Object.keys(meta)] }));
    const changed = PlummeStateContent.changedKeys(clientState, snapshot.data()?.state); // Keys equal to the server copy stay out: same document, but a small edit no longer uploads everything.
    transaction.set(stateRef, { state: Object.fromEntries(changed.map((key) => [key, clientState[key]])), ...meta }, { mergeFields: [...changed.map(key => `state.${key}`), ...Object.keys(meta)] });
  });
};

// Byraamedlemskap. Leses fra brukerens eget speil, ikke gjennom en Function: det er
// ett lite dokument per byraa, og skallet trenger svaret for aa vite om byraaflaten
// i det hele tatt skal vises.
const loadFirmMemberships = async () => {
  if (!runtime.db || !runtime.user) {
    runtime.firmMemberships = [];
    return;
  }
  try {
    const snapshot = await runtime.db.collection('users').doc(runtime.user.uid).collection('firmMemberships').get();
    runtime.firmMemberships = snapshot.docs
      .map((entry) => ({ firmId: entry.id, ...(entry.data() || {}) }))
      .filter((entry) => entry.status === 'active');
  } catch (error) {
    // Et byraa brukeren ikke er med i er ikke en feil verdt a vise. Flaten skjules,
    // og resten av appen gaar som for.
    console.warn('[plumme-firm] Kunne ikke lese byraamedlemskap:', error);
    runtime.firmMemberships = [];
  }
};

// Byraaene som jobber med foretaket brukeren staar i. Speilet skrives av backend
// ved opprettelse og aksept av oppdraget, og reglene aapner det bare for eier og
// admin — samme krets som kan akseptere et oppdrag, og samme krets som skal se
// dialogen med byraaet.
//
// En leser eller en vanlig ansatt faar `permission-denied` her. Det er ikke en
// feil verdt aa vise: da finnes det ingen chat aa aapne for dem, og resten av
// appen gaar som for.
const loadFirmEngagements = async () => {
  if (!runtime.db || !runtime.user || !runtime.activeBusinessId) {
    runtime.firmEngagements = [];
    return;
  }
  try {
    const snapshot = await runtime.db.collection('businesses').doc(runtime.activeBusinessId)
      .collection('accountingFirmEngagements').get();
    runtime.firmEngagements = snapshot.docs
      .map((entry) => ({ firmId: entry.id, ...(entry.data() || {}) }))
      .filter((entry) => entry.status === 'active');
  } catch (error) {
    runtime.firmEngagements = [];
  }
};

// Demokontoen kjennes pa custom claimet `demoAccount`, ikke pa e-postadressen.
// Claimet settes bare av `demo:seed`, og backend sjekker det uansett — dette er kun
// for a vite om navnefeltet skal vises. Leses det pa adressen, ville en demokonto
// seedet med en annen adresse sett ut som en vanlig bruker i flaten.
const loadDemoAccountClaim = async () => {
  try {
    const result = await runtime.auth?.currentUser?.getIdTokenResult?.();
    runtime.demoAccount = result?.claims?.demoAccount === true;
  } catch (error) {
    runtime.demoAccount = false;
  }
};

const loadBusinessAccess = async ({ preserveOnError = false } = {}) => {
  const firmMembershipsPromise = loadFirmMemberships();
  try {
    const payload = await authorizedPost('listBusinessAccessHttp');
    runtime.memberships = safeArray(payload.memberships);
    runtime.businessMembers = safeArray(payload.businessMembers);
    runtime.outgoingInvites = safeArray(payload.outgoingInvites);
    runtime.incomingInvites = safeArray(payload.incomingInvites);
  } catch (error) {
    await firmMembershipsPromise;
    if (preserveOnError) return false;
    runtime.error = error instanceof Error ? error.message : 'Could not load business access.';
    runtime.memberships = [];
    runtime.businessMembers = [];
    runtime.outgoingInvites = [];
    runtime.incomingInvites = [];
  }
  const stored = runtime.user ? localStorage.getItem(activeBusinessKey(runtime.user.uid)) : null;
  // Foretaket i adressen (`?f=`) er fanens eget valg og vinner over den delte
  // localStorage-nokkelen. Uten dette ville to vinduer i hvert sitt foretak
  // trukket hverandre til det sist valgte ved neste lasting.
  const requested = window.PlummeAppRoute?.businessFromSearch?.(window.location.search) || '';
  const fromUrl = requested ? runtime.memberships.find((membership) => membership.businessId === requested) : null;
  const active = fromUrl || runtime.memberships.find((membership) => membership.businessId === stored) || runtime.memberships[0] || null;
  runtime.activeBusinessId = active?.businessId || null;
  await Promise.all([firmMembershipsPromise, loadFirmEngagements()]);
  // En dyplenke skal ikke flytte det delte valget for de andre vinduene. Bare et
  // eksplisitt bytte (setActiveBusinessId) skriver noekkelen.
  if (runtime.user && runtime.activeBusinessId && !fromUrl) {
    localStorage.setItem(activeBusinessKey(runtime.user.uid), runtime.activeBusinessId);
  }
  return true;
};

const loadBillingSubscription = async () => {
  try {
    await initFirebase();
    if (!runtime.db || !runtime.user?.uid) {
      runtime.billing = null;
      return null;
    }
    const snapshot = await runtime.db.doc(`users/${runtime.user.uid}/billing/subscription`).get();
    runtime.billing = snapshot.exists ? { id: snapshot.id, ...(snapshot.data() || {}) } : null;
    return runtime.billing;
  } catch (error) {
    runtime.billing = null;
    return null;
  }
};

const activeBusinessMembership = () =>
  safeArray(runtime.memberships).find((membership) => membership.businessId === runtime.activeBusinessId) || null;
const isDemoSeedState = (metadata = {}) =>
  String(metadata?.source || metadata?.updatedBySource || '').trim() === 'demo-seed-script';
const canUseDemoSeedState = () => String(runtime.user?.email || '').trim().toLowerCase() === 'demo@plumme.no';

const repairKnownDesignMockBusinessEmail = (state = {}, { user = runtime.user, membership = activeBusinessMembership() } = {}) => {
  const currentEmail = normalizeEmail(state?.settings?.email);
  const ownerEmail = normalizeEmail(user?.email || membership?.email);
  if (!LEGACY_DESIGN_BUSINESS_EMAILS.has(currentEmail)) return { state, repaired: false };
  if (membership?.role !== 'owner') return { state, repaired: false };
  if (!isLikelyEmail(ownerEmail) || LEGACY_DESIGN_BUSINESS_EMAILS.has(ownerEmail)) return { state, repaired: false };
  return {
    state: {
      ...(state || {}),
      settings: {
        ...(state.settings || {}),
        email: ownerEmail,
        emailRepairedAt: nowIso(),
        emailRepairedFrom: currentEmail,
      },
    },
    repaired: true,
  };
};

// Keep each client state and listener when switching the active accounting client.
// See docs/features/regnskapsbyra-workspace-backlog.md, decision 9.4.
const multiClientStore = window.PlummeMultiClientStore
  ? window.PlummeMultiClientStore.createMultiClientStore()
  : null;
const subscribeBusinessState = () => {
  if (runtime.unsubscribeState) runtime.unsubscribeState();
  runtime.unsubscribeState = null;
  runtime.appState = null;
  runtime.appStateRevision = '';
  runtime.persistedAppStateRevision = '';
  runtime.persistedShardRevisions = {};
  runtime.personnelStateReadable = null;
  if (!runtime.db || !runtime.activeBusinessId) {
    runtime.loading = false;
    emitRuntime();
    return;
  }
  runtime.loading = true;
  emitRuntime();
  const subscribedBusinessId = runtime.activeBusinessId;
  const businessRef = runtime.db.collection('businesses').doc(subscribedBusinessId);

  // Merge separately authorized app and personnel documents into one view.
  let latestAppState = null;
  let latestPersonnelState = {};
  let appStateArrived = false;
  let latestAccountingEvents = [];
  let latestAccountingJournalStatus = null, latestAccountingBasisCoverage = null, latestAccountingCoverageBusinessId = '';
  let accountingJournalArrived = false, latestReadModelCoverage = null;
  const selectedJournalYear = () => Number(runtime.activeYearSelectionsByBusiness?.get(subscribedBusinessId)) || Number(latestAppState?.activeYear) || new Date().getFullYear();
  // Begge lytterne fyrer alltid — ogsa nar dokumentet mangler, og for viewer via
  // feilgrenen. A vente pa begge hindrer at lonnssiden blinker tom for den fylles.
  let personnelArrived = false;
  let stateFailed = false;
  let latestAppStateMeta = {};
  const failState = (error) => {
    if (runtime.activeBusinessId !== subscribedBusinessId) return;
    stateFailed = true;
    runtime.error = error.message || 'Could not load business state.'; runtime.appState = null; // Fatal: nothing published earlier stays visible.
    runtime.loading = false;
    emitRuntime();
  };
  // Shard-dokumentene app/state-markoren peker pa (stateShards.js). Et migrert foretak
  // publiseres ikke for shardet er lest; ellers ville receiptInbox blitt [] og kunne tommes.
  const shards = PlummeStateShards.createStateShardSubscription({
    businessRef, isCurrent: () => runtime.activeBusinessId === subscribedBusinessId, onChange: () => { rows.setLayout(latestAppStateMeta, shards.merge(latestAppState), selectedJournalYear()); loadAccountingJournal(selectedJournalYear()); publish(); }, onError: failState,
  });
  const rows = PlummeStateRows.createRowSubscription({
    businessRef, isCurrent: () => runtime.activeBusinessId === subscribedBusinessId, onChange: () => publish(), onError: failState,
    fiscalStart: (year, settings) => window.PlummeTrialBalanceContinuity?.fiscalBounds?.(settings, year)?.from || null });
  const ledger = PlummeLedgerBalances.createLedgerSubscription({ businessRef, isCurrent: () => runtime.activeBusinessId === subscribedBusinessId, onChange: () => publish(), onError: failState }); // Saldoer per maaned (ledgerBalances.js), aktiv bare med markoer.
  const publish = () => {
    if (runtime.activeBusinessId !== subscribedBusinessId) return;
    if (stateFailed || !appStateArrived || !shards.ready() || !rows.ready() || !ledger.ready()) return;
    if (runtime.journalDeferred && latestReadModelCoverage && !PlummeLedgerBalances.ledgerBalancesUsable(ledger.merge(latestAppState))) runtime.requestAccountingJournal();
    // Speed track S1: the journal is an overlay, not a precondition; until it is read the coverage is LOADING (blocked, shown as loading).
    const journalReady = accountingJournalArrived, journalYear = selectedJournalYear();
    const combined = { ...PlummePersonnelState.mergePersonnelState(ledger.merge(rows.merge(shards.merge(latestAppState))), latestPersonnelState),
      accountingEvents: journalReady ? latestAccountingEvents : [], accountingJournalStatus: journalReady ? latestAccountingJournalStatus : { status: 'LOADING', event_count: 0 }, accountingJournalDeferred: Boolean(runtime.journalDeferred), accountingReadModelCoverage: latestReadModelCoverage };
    const journalPeriod = runtime.accountingJournalFiscalYear ? window.PlummeTrialBalanceContinuity.fiscalBounds(combined.settings, journalYear) : undefined;
    combined.accountingBasisCoverage = journalReady ? window.PlummeAccountingBasisCoverage.acceptServerCoverage({ coverage: latestAccountingBasisCoverage,
      responseBusinessId: latestAccountingCoverageBusinessId, activeBusinessId: subscribedBusinessId, year: journalYear, period: journalPeriod,
      recordedSeq: combined.accountingRecordedSeqCounter, eventCount: latestAccountingEvents.length })
      : window.PlummeAccountingBasisCoverage.loadingCoverage(journalPeriod || { from: `${journalYear}-01-01`, to: `${journalYear}-12-31` });
    window.PlummeAccountingJournalRecovery.protectPublishedState(runtime, subscribedBusinessId, journalYear, combined);
    if (isDemoSeedState(latestAppStateMeta) && !canUseDemoSeedState()) {
      runtime.stateWriteBlockedBusinessIds?.add(subscribedBusinessId);
      runtime.appState = mergeState(null);
      runtime.loading = false;
      runtime.error = 'Dette foretaket peker på demo-data og er sperret for denne brukeren. Velg riktig foretak under Innstillinger > Foretak.';
      emitRuntime();
      return;
    }
    runtime.stateWriteBlockedBusinessIds?.delete(subscribedBusinessId);
    const repaired = repairKnownDesignMockBusinessEmail(mergeState(combined));
    const sessionYear = runtime.activeYearSelectionsByBusiness?.get(subscribedBusinessId), nextAppState = sessionYear ? { ...repaired.state, activeYear: sessionYear } : repaired.state;
    runtime.appState = PlummeStateContent.sameContent(nextAppState, runtime.appState) ? runtime.appState : nextAppState; // An echo of our own save: keep the object, so caches keyed on it survive.
    runtime.appStateRevision = latestAppStateMeta?.updatedAt?.toDate
      ? latestAppStateMeta.updatedAt.toDate().toISOString()
      : String(latestAppStateMeta?.updatedAt || '');
    runtime.persistedAppStateRevision = runtime.appStateRevision;
    runtime.persistedShardRevisions = shards.revisions();
    runtime.loading = false;
    runtime.error = null;
    // Hold plassen i flerklient-lageret oppdatert. Lytteren lever videre nar fanen
    // ikke er aktiv, og da er dette stedet den ferske staten havner.
    if (multiClientStore) {
      const slot = multiClientStore.openClient(subscribedBusinessId);
      if (slot) {
        slot.appState = runtime.appState;
        slot.appStateRevision = runtime.appStateRevision;
        slot.persistedAppStateRevision = runtime.persistedAppStateRevision;
        slot.persistedShardRevisions = runtime.persistedShardRevisions;
        slot.personnelStateReadable = runtime.personnelStateReadable;
        slot.loading = false;
        slot.error = null;
      }
    }
    emitRuntime();
    if (!personnelArrived) window.PlummeStartupPerformance?.mark('state-ready');
    else window.PlummeStartupPerformance?.mark('personnel-ready');
    if (repaired.repaired) {
      void persistCurrentState().catch((error) => {
        console.warn('[plumme-state] Kunne ikke reparere mock-epost paa foretaket:', error);
      });
    }
  };

  const unsubscribeApp = businessRef
    .collection('app')
    .doc('state')
    .onSnapshot(
      (snapshot) => {
        if (runtime.activeBusinessId !== subscribedBusinessId) return;
        const data = snapshot.exists ? snapshot.data() || {} : {};
        latestAppState = snapshot.exists ? data.state ?? null : null;
        latestAppStateMeta = snapshot.exists ? data : {};
        shards.setLayout(latestAppStateMeta); rows.setLayout(latestAppStateMeta, shards.merge(latestAppState), selectedJournalYear()); ledger.setLayout(latestAppStateMeta); runtime.rowDomains = PlummeStateRows.activeRowDomains(latestAppStateMeta);
        appStateArrived = true;
        loadAccountingJournal(selectedJournalYear()); publish(); // Publish before a requested journal finishes.
      },
      failState,
    );

  const unsubscribePersonnel = businessRef
    .collection('personnel')
    .doc('state')
    .onSnapshot(
      (snapshot) => {
        if (runtime.activeBusinessId !== subscribedBusinessId) return;
        latestPersonnelState = snapshot.exists ? snapshot.data()?.state ?? {} : {};
        runtime.personnelStateReadable = true;
        personnelArrived = true;
        publish();
      },
      (error) => {
        // A viewer's personnel denial must not block access to accounting data.
        if (runtime.activeBusinessId !== subscribedBusinessId) return;
        latestPersonnelState = {};
        // Never save default empty personnel arrays over data this user cannot read.
        runtime.personnelStateReadable = false;
        console.warn('[plumme-state] Kunne ikke lese personaldata (lonn og ansatte):', error);
        personnelArrived = true;
        publish();
      },
    );

  const journalLoader = createAccountingJournalLoader({
    runtime, businessId: subscribedBusinessId, getState: () => shards.merge(latestAppState),
    starting: () => { accountingJournalArrived = false; },
    loaded: () => { accountingJournalArrived = true; publish(); },
    success: (payload) => {
      latestAccountingEvents = safeArray(payload?.accountingEvents).sort((left, right) => num(left.recorded_seq) - num(right.recorded_seq)); latestAccountingJournalStatus = payload?.accountingJournalStatus || null;
      latestAccountingBasisCoverage = payload?.accountingBasisCoverage || null; latestAccountingCoverageBusinessId = String(payload?.businessId || '');
      accountingJournalArrived = true;
      publish();
    },
    failure: (error, year) => {
      if (window.PlummeAccountingJournalRecovery.isAccessError(error)) { failState(error); return; }
      window.PlummeAccountingJournalRecovery.fail(runtime, subscribedBusinessId, year, error);
      latestAccountingEvents = []; latestAccountingJournalStatus = { status: 'INCOMPLETE', event_count: 0 };
      latestAccountingBasisCoverage = null; latestAccountingCoverageBusinessId = subscribedBusinessId;
      accountingJournalArrived = true; publish();
    },
  });
  const coverageLoader = createAccountingCoverageLoader({ runtime, businessId: subscribedBusinessId, getState: () => shards.merge(latestAppState),
    starting: () => { latestReadModelCoverage = null; }, success: (payload) => { latestReadModelCoverage = payload; if (!['COMPLETE', 'EMPTY'].includes(payload?.accountingBasisCoverage?.status)) runtime.requestAccountingJournal(); publish(); }, failure: () => { latestReadModelCoverage = { finalized: false }; runtime.requestAccountingJournal(); } });
  const loadAccountingJournal = window.PlummeJournalDemand.attach({ ready: () => appStateArrived && shards.ready(), loader: journalLoader, coverage: coverageLoader, metadata: () => latestAppStateMeta, state: () => shards.merge(latestAppState), year: selectedJournalYear });
  runtime.retryAccountingJournal = () => journalLoader.retry(selectedJournalYear());
  runtime.loadStateRow = (key, id) => key === 'bankStatements' ? rows.loadBankStatement(id) : rows.loadRow(key, id);
  runtime.resubscribeAccountingJournal = (year) => {
    if (runtime.activeBusinessId !== subscribedBusinessId) return; rows.setLayout(latestAppStateMeta, shards.merge(latestAppState), year); loadAccountingJournal(year);
  };

  runtime.unsubscribeState = () => {
    unsubscribeApp();
    unsubscribePersonnel();
    journalLoader.stop(); coverageLoader.stop(); runtime.retryAccountingJournal = null; runtime.loadStateRow = null; runtime.requestAccountingJournal = null; runtime.journalDeferred = false;
    if (runtime.resubscribeAccountingJournal) runtime.resubscribeAccountingJournal = null;
    shards.stop(); rows.stop(); ledger.stop(); runtime.rowDomains = [];
  };
  if (multiClientStore) {
    const slot = multiClientStore.setActive(subscribedBusinessId);
    if (slot) slot.unsubscribe = runtime.unsubscribeState;
    releaseOverflowClients();
  }
};

const saveState = (nextState, immediate = false) => {
  window.PlummeAccountingJournalRecovery.assertWritable(runtime);
  if (runtime.stateWriteBlockedBusinessIds?.has(runtime.activeBusinessId)) {
    runtime.error = 'Dette foretaket er skrivebeskyttet fordi det peker på demo-data for en annen bruker.';
    emitRuntime();
    throw new Error(runtime.error);
  }
  if (hasClientServerOwnedStateChange(runtime.appState, nextState)) {
    runtime.error = 'Denne endringen må lagres av regnskapsserveren og ble ikke utført lokalt.';
    console.error('[plumme-state] Avviste klientendring av servereid regnskapsstate.');
    emitRuntime();
    throw new Error(runtime.error);
  }
  runtime.appState = mergeState(nextState);
  emitRuntime();
  const businessId = runtime.activeBusinessId;
  const slot = multiClientStore?.openClient(businessId) || null;
  if (slot) {
    slot.appState = runtime.appState;
    slot.personnelStateReadable = runtime.personnelStateReadable;
    if (slot.saveTimer) window.clearTimeout(slot.saveTimer);
  } else if (runtime.saveTimer) window.clearTimeout(runtime.saveTimer);
  const persistLatestState = () => {
    if (slot) slot.saveTimer = null;
    else runtime.saveTimer = null;
    // Read at timer-fire time. A Function response or Firestore snapshot may have
    // replaced the state while this save was waiting; persisting the captured
    // pre-response object would restore stale client-owned fields.
    const state = slot?.appState || (runtime.activeBusinessId === businessId ? runtime.appState : null);
    return persistCurrentState({ businessId, state });
  };
  if (immediate) {
    void persistLatestState().catch((error) => {
      console.warn('[plumme-state] Lagring av foretaksstate feilet:', error);
      if (!window.PlummeAccountingJournalRecovery.getIssue(runtime, businessId)) runtime.error = error.message || 'Could not save state.';
      emitRuntime();
    });
    return;
  }
  const timer = window.setTimeout(() => {
    void persistLatestState().catch((error) => {
      console.warn('[plumme-state] Lagring av foretaksstate feilet:', error);
      if (!window.PlummeAccountingJournalRecovery.getIssue(runtime, businessId)) runtime.error = error.message || 'Could not save state.';
      emitRuntime();
    });
  }, 500);
  if (slot) slot.saveTimer = timer;
  else runtime.saveTimer = timer;
};

const updateState = (updater, immediate = false) => {
  const base = mergeState(runtime.appState);
  const next = typeof updater === 'function' ? updater(base) : updater;
  saveState(next, immediate);
};

const setLocalActiveYear = (year) => {
  const value = Number(year);
  if (!runtime.activeBusinessId || !Number.isInteger(value)) return;
  runtime.activeYearSelectionsByBusiness?.set(runtime.activeBusinessId, value); runtime.loading = typeof runtime.resubscribeAccountingJournal === 'function'; runtime.error = null;
  runtime.appState = { ...mergeState(runtime.appState), activeYear: value };
  const slot = multiClientStore?.openClient(runtime.activeBusinessId);
  if (slot) slot.appState = runtime.appState;
  runtime.resubscribeAccountingJournal?.(value);
  emitRuntime();
};

const applyVatMutationResult = (result) => {
  if (result?.state) runtime.appState = mergeAppMutationState(result.state);
  emitRuntime();
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return result;
};
